// src/config/semantic.config.ts
// CRA-friendly semantic config (reads REACT_APP_*). Safe for Sandbox.
// Do NOT import import.meta.env here; CRA compiles process.env at build time.

type Boolish = string | undefined | null;

// Explicit boolean parser: honors "true"/"false" (and 1/0/yes/no). Falls back to default only if unset/unknown.
const asBool = (v: Boolish, d = false) => {
  if (v == null) return d;
  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return d;
};

const asNum = (v: string | undefined, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const clamp01 = (x: number) => Math.max(0, Math.min(0.99, x));

// Stage toggle
const STAGE_ENABLED = asBool(process.env.REACT_APP_SEMANTIC_STAGE, true);

// Thresholds
const TAU_DEFAULT = 0.54; // backstop trigger-path cosine cutoff
const TAU = asNum(process.env.REACT_APP_SEMANTIC_TAU, TAU_DEFAULT);

// Title-only comparisons: default to the same as TAU (no +0.06 bump)
const TAU_TITLE_ENV = process.env.REACT_APP_SEMANTIC_TAU_TITLE;
const TAU_TITLE = clamp01(TAU_TITLE_ENV !== undefined ? asNum(TAU_TITLE_ENV, TAU) : TAU);

// Trigger-path minimum token overlap (after normalization/synonyms).
const MIN_OVERLAP = asNum(process.env.REACT_APP_SEMANTIC_MIN_OVERLAP, 1);

// In Sandbox, it’s safer to default to Starter-only to avoid bleed during tuning.
// Flip via env if you want to test broader packets.
const STARTER_ONLY = asBool(process.env.REACT_APP_SEMANTIC_STARTER_ONLY, true);

export const semanticConfig = {
  enabled: STAGE_ENABLED,
  tau: TAU,
  tauTitle: TAU_TITLE,
  minOverlap: MIN_OVERLAP,
  starterOnly: STARTER_ONLY,
} as const;

// Optional: expose to window in dev for quick sanity checks + doc runtime overrides
if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
  (window as any).semanticConfig = {
    ...semanticConfig,
    // Runtime overrides (handled in semantic-matcher.ts):
    // localStorage:
    //   cb_sem_tau        -> number (overrides tau)
    //   cb_sem_tautitle   -> number (overrides tauTitle)
    //   cb_sem_minOverlap -> number (overrides minOverlap)
    //   cb_debug_semantic -> "true"/"false" to toggle top5 debug logs
  };
}
